import { useEffect, useMemo, useState } from 'react';
import {
  fetchSiteBranding,
  getSearchQueryFromUrl,
  navigate,
  navigateToLiveSearch,
  navigateToMypageHub,
  openChannelPanel,
  logoutAccount,
  resolveLogoUrl,
  showOneliveToast,
} from './api';
import { oneliveAssets } from './assets';
import { handleBroadcastStart, handleLoginRequired } from './liveEntryGuard';
import { OneliveLogoLink } from './OneliveLogo';
import { useLiveUiMessages } from './useLiveUiMessages';
import { useOneliveSession } from './useOneliveSession';

interface OneliveHeaderProps {
  siteName?: string;
  logo?: string;
  mobileLogo?: string;
  user?: { uuid?: string; name?: string; email?: string } | null;
}

const DEFAULT_LOGO = '/assets/templates/onelive-basic/ftv/logo.svg';
const MOBILE_MQ = '(max-width: 768px)';

export default function OneliveHeader({
  siteName = '원라이브TV',
  logo,
  mobileLogo,
  user,
}: OneliveHeaderProps) {
  const [keyword, setKeyword] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [branding, setBranding] = useState<{ site_name?: string; logo_pc?: string; logo_mobile?: string }>({});

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    let mounted = true;
    fetchSiteBranding().then((data) => {
      if (mounted && data) setBranding(data);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const syncKeyword = () => {
      const q = getSearchQueryFromUrl();
      if (q) setKeyword(q);
    };
    syncKeyword();
    window.addEventListener('popstate', syncKeyword);
    return () => window.removeEventListener('popstate', syncKeyword);
  }, []);

  const brand = useMemo(() => {
    const raw = branding.site_name || siteName;
    return raw || '원라이브TV';
  }, [branding.site_name, siteName]);

  const resolvedLogo = useMemo(() => {
    if (isMobile) {
      return resolveLogoUrl(mobileLogo || branding.logo_mobile || logo || branding.logo_pc || DEFAULT_LOGO);
    }
    return resolveLogoUrl(logo || branding.logo_pc || mobileLogo || branding.logo_mobile || DEFAULT_LOGO);
  }, [isMobile, logo, mobileLogo, branding.logo_pc, branding.logo_mobile]);

  const runSearch = () => {
    const term = keyword.trim();
    if (term.length < 2) {
      showOneliveToast('두글자 이상 입력해 주세요.', 'warning');
      return;
    }
    setMobileSearchOpen(false);
    navigateToLiveSearch(term);
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch();
  };

  const liveUiMessages = useLiveUiMessages();
  const { user: sessionUser, isAuthed, authReady } = useOneliveSession();
  const activeUser = authReady ? sessionUser : (user ?? sessionUser);
  const mypageInitial = (activeUser?.name || activeUser?.email || 'M').charAt(0).toUpperCase();

  const isLoggedIn = Boolean(activeUser?.uuid);

  const onBroadcastClick = () => {
    handleBroadcastStart(liveUiMessages);
  };

  const onShopClick = () => {
    if (!authReady) {
      navigate('/shop');
      return;
    }
    if (!isAuthed) {
      handleLoginRequired(liveUiMessages, '/shop');
      return;
    }
    navigate('/shop');
  };

  const onLogoutClick = async () => {
    await logoutAccount();
    showOneliveToast('로그아웃 되었습니다.', 'info');
  };

  return (
    <>
      <header id="onelive-header" className="onelive-header">
        <div className="onelive-header-mobile-left">
          <button
            type="button"
            className="onelive-header-icon-btn"
            onClick={openChannelPanel}
            aria-label="추천채널"
            title="추천채널"
          >
            <img src={oneliveAssets.menuMobile} alt="" className="onelive-header-icon-img is-light" />
          </button>
        </div>

        <h1 className="onelive-logo">
          <OneliveLogoLink src={resolvedLogo} alt={brand} />
        </h1>

        <div className="onelive-header-actions">
          <form className="onelive-search" onSubmit={onSearch}>
            <input
              type="text"
              placeholder="두글자 이상 입력하세요."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              aria-label="검색"
            />
            <button type="submit" className="onelive-search-btn" aria-label="검색" title="검색">
              <img src={oneliveAssets.search} alt="" />
            </button>
          </form>

          <div className="onelive-header-btns">
            <button
              type="button"
              className="onelive-btn-header onelive-btn-header-action"
              onClick={onBroadcastClick}
              title="방송시작"
            >
              <img src={oneliveAssets.movieOn} alt="" className="onelive-header-ico" />
              방송시작
            </button>
            <button
              type="button"
              className="onelive-btn-header onelive-btn-header-action onelive-btn-header-shop"
              onClick={onShopClick}
            >
              <img src={oneliveAssets.gift} alt="" className="onelive-header-ico" />
              아이템샵
            </button>
          </div>

          <div className="onelive-auth-btns">
            {isLoggedIn ? (
              <>
                <button
                  type="button"
                  className="onelive-btn-header onelive-btn-header-mypage"
                  onClick={() => navigateToMypageHub()}
                  title="마이페이지"
                >
                  <span className="onelive-btn-mypage-avatar" aria-hidden="true">
                    {mypageInitial}
                  </span>
                  마이페이지
                </button>
                <button
                  type="button"
                  className="onelive-btn-logout"
                  onClick={() => void onLogoutClick()}
                  title="로그아웃"
                >
                  로그아웃
                </button>
              </>
            ) : (
              <>
                <button type="button" className="onelive-btn-login" onClick={() => navigate('/login')}>로그인</button>
                <button type="button" className="onelive-btn-register" onClick={() => navigate('/register')}>회원가입</button>
              </>
            )}
          </div>
        </div>

        <div className="onelive-header-mobile-right">
          <button
            type="button"
            className="onelive-header-icon-btn"
            onClick={onBroadcastClick}
            aria-label="방송시작"
            title="방송시작"
          >
            <img src={oneliveAssets.movie} alt="" className="onelive-header-icon-img is-light" />
          </button>
          <button
            type="button"
            className={`onelive-header-icon-btn${mobileSearchOpen ? ' is-active' : ''}`}
            onClick={() => setMobileSearchOpen((v) => !v)}
            aria-label="검색"
            aria-expanded={mobileSearchOpen}
            title="검색"
          >
            <img src={oneliveAssets.searchWhite} alt="" className="onelive-header-icon-img is-light" />
          </button>
        </div>
      </header>

      {mobileSearchOpen && (
        <form className="onelive-mobile-search-bar" onSubmit={onSearch}>
          <input
            type="text"
            placeholder="두글자 이상 입력하세요."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            aria-label="검색"
            autoFocus
          />
          <button type="submit" className="onelive-mobile-search-submit" aria-label="검색">
            <img src={oneliveAssets.searchWhite} alt="" />
          </button>
        </form>
      )}
    </>
  );
}
